import { timeLineDataSetSchema, timeLineItemSchema } from '@tempo-io/adaptive-planner-dto';
import { z } from 'zod';
import { fetchChunksWithConcurrencyLimit } from '@/common/api/chunkedFetch';
import { tempoApi } from '@/common/api/tempoApi';
import { BULK_FETCH_MAX_ITEMS } from '@/common/constants';
import { chunkBy } from '@/common/utils/chunkBy';
import {
  buildScenarioItemFiltersQueryString,
  type ScenarioItemFilters,
} from '@/features/planner/api/adaptive-planner/scenarioItemFilters';

const getDataSet = async ({
  loopId,
  scenarioId,
  filters,
  order,
}: {
  loopId: number;
  scenarioId: number;
  filters?: ScenarioItemFilters;
  order?: string;
}) => {
  const response = await tempoApi()
    .get(
      `api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/timeline-items/data-set${buildScenarioItemFiltersQueryString(filters, order)}`,
    )
    .json();
  return z.array(timeLineDataSetSchema).parse(response);
};

const bulkFetch = async ({
  loopId,
  scenarioId,
  itemIds,
  signal,
}: {
  loopId: number;
  scenarioId: number;
  itemIds: number[];
  signal?: AbortSignal;
}) => {
  const response = await tempoApi()
    .post(
      `api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/timeline-items/bulk-fetch`,
      { json: itemIds, signal },
    )
    .json();
  return z.array(timeLineItemSchema).parse(response);
};

// Chunked variant — splits requests into `BULK_FETCH_MAX_ITEMS`-sized batches and
// merges the responses. Use whenever the id list is caller-driven and unbounded: an
// oversized body is rejected by the gateway's WAF (SizeRestrictions_BODY, 8KB) before
// it reaches the API, which surfaces as a timeline of blank rows.
const bulkFetchChunked = async ({
  loopId,
  scenarioId,
  itemIds,
  signal,
}: {
  loopId: number;
  scenarioId: number;
  itemIds: number[];
  signal?: AbortSignal;
}) => {
  if (itemIds.length === 0) return [];
  if (itemIds.length <= BULK_FETCH_MAX_ITEMS) {
    return bulkFetch({ loopId, scenarioId, itemIds, signal });
  }
  const responses = await fetchChunksWithConcurrencyLimit(
    chunkBy(itemIds, BULK_FETCH_MAX_ITEMS),
    (chunk) => bulkFetch({ loopId, scenarioId, itemIds: chunk, signal }),
  );
  return responses.flat();
};

export const LoopPlannerTimelineItemsApi = {
  getDataSet,
  bulkFetch,
  bulkFetchChunked,
} as const;
